import React from 'react';

import { GroupRole } from '@/__generated__/graphql';

import { ActivityChip, ExpertAvatar } from './primitives';
import type { ActivityChipVariant } from './primitives';
import type { TeamMemberInfo } from './useDashboardTeamMembers';

interface TeamMemberRowProps {
  member: TeamMemberInfo;
}

const ROLE_LABELS: Record<GroupRole, string> = {
  [GroupRole.Owner]: 'Owner',
  [GroupRole.Admin]: 'Admin',
  [GroupRole.Member]: 'Member',
  [GroupRole.Viewer]: 'Viewer',
};

function getActivity(member: TeamMemberInfo): { variant: ActivityChipVariant; label: string } {
  const { consultationCount, hasUpcomingConsultations } = member;
  const label = `${consultationCount} consultation${consultationCount === 1 ? '' : 's'}`;
  const variant: ActivityChipVariant = hasUpcomingConsultations
    ? 'active'
    : consultationCount > 0
      ? 'recent'
      : 'inactive';
  return { variant, label };
}

export function TeamMemberRow({ member }: TeamMemberRowProps): React.ReactNode {
  const { variant, label } = getActivity(member);

  return (
    <div className="last:border-b-0 flex items-center gap-12 border-b border-surface-border py-8">
      <ExpertAvatar
        firstName={member.firstName}
        lastName={member.lastName}
        name={member.name}
        pictureUrl={member.pictureUrl}
        size={32}
      />
      <div className="flex min-w-0 flex-1 items-center justify-between gap-8">
        <div className="min-w-0">
          <p className="truncate text-14 font-semibold leading-tight">{member.name}</p>
          <p className="text-12 text-on-surface-muted">{ROLE_LABELS[member.role]}</p>
        </div>
        <ActivityChip variant={variant} label={label} />
      </div>
    </div>
  );
}
